import React, { ReactNode, useState } from 'react'

import {
  AdvancedFiltersDrawer,
  FilterItem,
  ScrollableOption,
  ScrollableOptionList,
} from './index'
import {
  SearchableSelect,
  SearchableSelectOption,
} from '@/components/newRedesign/components/SearchableSelect'

export type FilterType = 'searchable' | 'scrollable' | 'custom'

export interface BaseFilterConfig {
  id: string
  type: FilterType
  icon: ReactNode
  title: string
  label: string
}

export interface SearchableFilterConfig extends BaseFilterConfig {
  type: 'searchable'
  fetcher: (_search: string) => Promise<SearchableSelectOption[]>
  containerHeight?: number
  searchPlaceholder?: string
  hideSearch?: boolean
}

export interface ScrollableFilterConfig extends BaseFilterConfig {
  type: 'scrollable'
  options: ScrollableOption[] | { value: string; label: string }[]
  containerHeight?: number
  isLoading?: boolean
}

export interface CustomFilterConfig extends BaseFilterConfig {
  type: 'custom'
  component: ReactNode
}

export type FilterConfig =
  | SearchableFilterConfig
  | ScrollableFilterConfig
  | CustomFilterConfig

export type FilterValues = Record<
  string,
  string | number | null | (string | number)[]
>

export type FilterLabels = Record<string, string | null>

interface GenericAdvancedFiltersProps {
  /**
   * Array of filter configurations
   */
  filters: FilterConfig[]
  /**
   * Current filter values
   */
  values: FilterValues
  /**
   * Callback when values change (optional, useful for external state tracking)
   */
  onValuesChange?: (_values: FilterValues) => void
  /**
   * Callback when apply button is clicked
   * Return false to prevent closing the drawer (e.g., validation failed)
   */
  onApply: (_values: FilterValues) => boolean | undefined
  /**
   * Callback when clear button is clicked
   */
  onClear: () => void
  /**
   * Number of active filters (for badge)
   */
  activeFiltersCount?: number
  /**
   * Drawer title
   */
  title?: string
  /**
   * Trigger button label
   */
  triggerLabel?: string
  /**
   * Apply button label
   */
  applyLabel?: string
  /**
   * Clear button label
   */
  clearLabel?: string
}

/**
 * GenericAdvancedFilters - Highly reusable advanced filters component
 *
 * This component provides a complete solution for advanced filters with:
 * - Searchable selects (with async data fetching)
 * - Simple scrollable lists
 * - Custom filter components
 * - Automatic label tracking
 * - Staged changes (apply/cancel)
 *
 * Note: The clear filters button should be added at the page level,
 * next to this component, using the ClearFiltersButton component.
 *
 * Usage:
 * ```tsx
 * const filterConfigs: FilterConfig[] = [
 *   {
 *     id: 'company',
 *     type: 'searchable',
 *     icon: <Building2 className="h-4 w-4" />,
 *     title: 'Compañía',
 *     label: 'Seleccionar compañía',
 *     fetcher: searchCompanies,
 *     containerHeight: 200,
 *   },
 *   {
 *     id: 'category',
 *     type: 'scrollable',
 *     icon: <Tag className="h-4 w-4" />,
 *     title: 'Categoría',
 *     label: 'Seleccionar categoría',
 *     options: categoryOptions,
 *     containerHeight: 180,
 *   },
 * ]
 *
 * <GenericAdvancedFilters
 *   filters={filterConfigs}
 *   values={filterValues}
 *   onValuesChange={setFilterValues}
 *   onApply={handleApply}
 *   onClear={handleClear}
 *   activeFiltersCount={5}
 * />
 * ```
 */
export function GenericAdvancedFilters({
  filters,
  values,
  onValuesChange,
  onApply,
  onClear,
  activeFiltersCount = 0,
  title = 'Filtros avanzados',
  triggerLabel = 'Filtros avanzados',
  applyLabel = 'Aplicar',
  clearLabel = 'Limpiar',
}: GenericAdvancedFiltersProps) {
  // Local state for staged values
  const [localValues, setLocalValues] = useState<FilterValues>(values)

  // Update local values when parent values change
  React.useEffect(() => {
    setLocalValues(values)
  }, [values])

  // Handle value change for a specific filter
  const handleFilterChange = (
    filterId: string,
    value: string | number | null | (string | number)[]
  ) => {
    setLocalValues((prev) => ({
      ...prev,
      [filterId]: value,
    }))
  }

  // (no label tracking - FilterItem displays only title and component)

  // Handle apply
  const handleApply = () => {
    onValuesChange?.(localValues)
    return onApply(localValues)
  }

  // Handle clear
  const handleClear = () => {
    const clearedValues: FilterValues = {}
    filters.forEach((filter) => {
      clearedValues[filter.id] = null
    })
    setLocalValues(clearedValues)
    onClear()
  }

  // Render a single filter based on its type
  const renderFilter = (filter: FilterConfig) => {
    const value = localValues[filter.id] ?? null

    switch (filter.type) {
      case 'searchable':
        return (
          <SearchableSelect
            fetcher={filter.fetcher}
            name={filter.id}
            onChange={(newValue) => handleFilterChange(filter.id, newValue)}
            placeholder={filter.label}
            searchPlaceholder={filter.searchPlaceholder}
            value={value as string | number | null}
          />
        )

      case 'scrollable':
        return (
          <ScrollableOptionList
            collapsed
            containerHeight={filter.containerHeight || 180}
            isLoading={filter.isLoading}
            onChange={(newValue) => {
              handleFilterChange(filter.id, newValue)
              // For scrollable lists, we update the value; label tracking removed
            }}
            options={filter.options.map((opt) =>
              'id' in opt ? opt : { id: opt.value, label: opt.label }
            )}
            placeholder={filter.label}
            value={value as string | number | null}
          />
        )

      case 'custom':
        return filter.component

      default:
        return null
    }
  }

  return (
    <AdvancedFiltersDrawer
      activeFiltersCount={activeFiltersCount}
      applyLabel={applyLabel}
      clearLabel={clearLabel}
      onApply={handleApply}
      onClear={handleClear}
      title={title}
      triggerLabel={triggerLabel}
    >
      {filters.map((filter, index) => (
        <React.Fragment key={filter.id}>
          <FilterItem icon={filter.icon} title={filter.title}>
            {renderFilter(filter)}
          </FilterItem>

          {/* Divider between filters */}
          {index < filters.length - 1 && (
            <div className="h-px w-full bg-neutral-300" />
          )}
        </React.Fragment>
      ))}
    </AdvancedFiltersDrawer>
  )
}
